/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Archetype wheel order (clockwise from top):
 * 1. Visionary Strategist (top)
 * 2. Bold Initiator (top-right)
 * 3. Encouraging Connector (bottom-right)
 * 4. Steady Builder (bottom)
 * 5. Reflective Guide (bottom-left)
 * 6. Curious Explorer (top-left)
 */

interface WheelSegment {
  key: string;
  typeNumber: number;
  label: string;
  labelLine2: string;
  startAngle: number;
}

const SEGMENTS: WheelSegment[] = [
  { key: 'visionary_strategist', typeNumber: 1, label: 'Visionary', labelLine2: 'Strategist', startAngle: -90 },
  { key: 'bold_initiator', typeNumber: 2, label: 'Bold', labelLine2: 'Initiator', startAngle: -30 },
  { key: 'encouraging_connector', typeNumber: 3, label: 'Encouraging', labelLine2: 'Connector', startAngle: 30 },
  { key: 'steady_builder', typeNumber: 4, label: 'Steady', labelLine2: 'Builder', startAngle: 90 },
  { key: 'reflective_guide', typeNumber: 5, label: 'Reflective', labelLine2: 'Guide', startAngle: 150 },
  { key: 'curious_explorer', typeNumber: 6, label: 'Curious', labelLine2: 'Explorer', startAngle: 210 },
];

@Component({
  selector: 'app-archetype-wheel',
  standalone: true,
  imports: [CommonModule],
  template: `
    <svg [attr.viewBox]="'-260 -260 520 520'" xmlns="http://www.w3.org/2000/svg" class="archetype-wheel">
      <!-- Segments -->
      <g *ngFor="let seg of segments">
        <path
          [attr.d]="getSegmentPath(seg.startAngle, seg.startAngle + 60)"
          [attr.fill]="getSegmentColor(seg.key)"
          stroke="white"
          stroke-width="2"
        />
        <!-- Labels inside segment -->
        <g [attr.transform]="getTextTransform(seg.startAngle + 30)">
          <text text-anchor="middle" [attr.fill]="getTextColor(seg.key)" font-size="11" font-weight="700" dy="-18">
            TYPE {{ seg.typeNumber }}
          </text>
          <text text-anchor="middle" [attr.fill]="getTextColor(seg.key)" font-size="14" font-style="italic" dy="0">
            {{ seg.label }}
          </text>
          <text text-anchor="middle" [attr.fill]="getTextColor(seg.key)" font-size="14" font-style="italic" dy="17">
            {{ seg.labelLine2 }}
          </text>
        </g>
      </g>

      <!-- Center circle (white) -->
      <circle cx="0" cy="0" r="52" fill="white" />

      <!-- Steve's designer PNGs around the outside. Each is 178x178 with the -->
      <!-- green-circle background already baked in, so no per-icon SVG art -->
      <!-- needed. Centered on the icon position via x/y = -size/2. -->
      <g *ngFor="let seg of segments">
        <g [attr.transform]="getIconTransform(seg.startAngle + 30)">
          <image
            [attr.href]="'assets/images/archetype/' + getIconFilename(seg.key)"
            x="-28" y="-28" width="56" height="56"
          />
        </g>
      </g>
    </svg>
  `,
  styles: [`
    :host {
      display: block;
    }
    .archetype-wheel {
      width: 100%;
      height: auto;
      max-width: 420px;
    }
  `],
})
export class ArchetypeWheelComponent {
  /** Accepts either snake_case key or display name (e.g. "Bold Initiator") */
  @Input() primary: string = '';
  @Input() secondary: string = '';

  segments = SEGMENTS;

  private readonly INNER_RADIUS = 55;
  private readonly OUTER_RADIUS = 170;
  private readonly ICON_RADIUS = 205;

  private toKey(value: string): string {
    if (!value) return '';
    return value.toLowerCase().replace(/\s+/g, '_');
  }

  // Primary slice: brand IPSAT green. Overlay: a lighter tint of the SAME hue
  // (Steve 2026-06-25 — previous #7dd3c0 was a leftover teal from the old
  // #1ab394 era and read as off-brand). Inactive: neutral grey.
  getSegmentColor(key: string): string {
    if (key === this.toKey(this.primary)) return '#44b25d';
    if (key === this.toKey(this.secondary)) return '#9dd2a9';
    return '#e0e0e0';
  }

  getTextColor(key: string): string {
    if (key === this.toKey(this.primary)) return '#ffffff';
    return '#333333';
  }

  getSegmentPath(startAngle: number, endAngle: number): string {
    const r1 = this.INNER_RADIUS;
    const r2 = this.OUTER_RADIUS;
    const a1 = (startAngle * Math.PI) / 180;
    const a2 = (endAngle * Math.PI) / 180;

    const x1 = r1 * Math.cos(a1);
    const y1 = r1 * Math.sin(a1);
    const x2 = r2 * Math.cos(a1);
    const y2 = r2 * Math.sin(a1);
    const x3 = r2 * Math.cos(a2);
    const y3 = r2 * Math.sin(a2);
    const x4 = r1 * Math.cos(a2);
    const y4 = r1 * Math.sin(a2);

    return `M ${x1} ${y1} L ${x2} ${y2} A ${r2} ${r2} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${r1} ${r1} 0 0 0 ${x1} ${y1} Z`;
  }

  getTextTransform(midAngle: number): string {
    const r = (this.INNER_RADIUS + this.OUTER_RADIUS) / 2;
    const a = (midAngle * Math.PI) / 180;
    const x = r * Math.cos(a);
    const y = r * Math.sin(a);
    return `translate(${x}, ${y})`;
  }

  getIconTransform(midAngle: number): string {
    const a = (midAngle * Math.PI) / 180;
    const x = this.ICON_RADIUS * Math.cos(a);
    const y = this.ICON_RADIUS * Math.sin(a);
    return `translate(${x}, ${y})`;
  }

  /** Map segment key → designer PNG filename in src/assets/images/archetype/. */
  getIconFilename(key: string): string {
    switch (key) {
      case 'visionary_strategist': return 'visionary-strategist.png';
      case 'bold_initiator': return 'bold-initiator.png';
      case 'encouraging_connector': return 'encouraging-connector.png';
      case 'steady_builder': return 'steady-builder.png';
      case 'reflective_guide': return 'reflective-guide.png';
      case 'curious_explorer': return 'curious-explorer.png';
      default: return 'visionary-strategist.png';
    }
  }
}
